import React, { useMemo, useState } from 'react';
import { Button, Drawer, Empty, Progress, Table, Tag } from 'antd';
import { format } from 'date-fns';
import AsrChamberIssueEditor from './AsrChamberIssueEditor';

const formatDate = (value) => {
  if (!value) return 'Not set';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 'Not set' : format(parsed, 'dd/MM/yyyy');
};

const chamberProgress = (chamber) => {
  const issues = chamber.issues || [];
  if (!issues.length) return null;
  const solved = issues.filter((i) => String(i.solution || '').trim().length > 0).length;
  return { solved, total: issues.length, percent: Math.round((solved / issues.length) * 100) };
};

// Shows every chamber in an ASR session with its assigned technicians. Clicking a
// chamber opens the issue/solution editor sourced from that chamber's latest service form.
const AsrSessionDetail = ({
  visible,
  session,
  tenantId,
  readOnly = false,
  onClose,
  onSaveChamberIssues,
  onComplete,
  onDownload,
}) => {
  const [activeChamber, setActiveChamber] = useState(null);
  const sortedChambers = useMemo(
    () => [...(session?.chambers || [])].sort((left, right) =>
      String(left.chamberName || '').localeCompare(String(right.chamberName || ''), undefined, {
        numeric: true,
        sensitivity: 'base',
      }),
    ),
    [session?.chambers],
  );

  const columns = useMemo(
    () => [
      {
        title: 'Chamber',
        dataIndex: 'chamberName',
        key: 'chamberName',
        width: 220,
      },
      {
        title: 'Technician(s)',
        key: 'technicians',
        render: (_, row) => (
          <div className="asr-tech-tags">
            {(row.technicianNames || []).length
              ? row.technicianNames.map((name) => (
                  <Tag key={name} color="blue">
                    {name}
                  </Tag>
                ))
              : <span className="asr-muted">No technician assigned</span>}
          </div>
        ),
      },
      {
        title: 'Issues Progress',
        key: 'progress',
        width: 220,
        render: (_, row) => {
          const progress = chamberProgress(row);
          if (!progress) return <span className="asr-muted">Not filled yet</span>;
          return (
            <div className="asr-progress-cell">
              <Progress percent={progress.percent} size="small" />
              <span className="asr-progress-count">
                {progress.solved}/{progress.total} solved
              </span>
            </div>
          );
        },
      },
      {
        title: '',
        key: 'action',
        width: 160,
        render: (_, row) =>
          readOnly ? null : (
            <Button type="primary" onClick={() => setActiveChamber(row)}>
              Fill Solutions
            </Button>
          ),
      },
    ],
    [readOnly],
  );

  if (!session) return null;

  return (
    <>
      <Drawer
        title={`Site ASR — ${session.siteName}`}
        visible={visible}
        onClose={onClose}
        width={860}
        className="asr-session-drawer"
        extra={
          <div className="asr-drawer-actions">
            {session.status === 'completed' && (
              <Button onClick={() => onDownload(session)}>Download Report</Button>
            )}
            {!readOnly && (
              <Button
                type="primary"
                onClick={() => onComplete(session, session.status !== 'completed')}
              >
                {session.status === 'completed' ? 'Reopen Session' : 'Mark Completed'}
              </Button>
            )}
          </div>
        }
      >
        <div className="asr-drawer-summary">
          <div className="asr-drawer-summary-item">
            <div className="label">Status</div>
            <Tag color={session.status === 'completed' ? 'green' : 'gold'} className="asr-status-tag">
              {session.status === 'completed' ? 'Completed' : 'Draft'}
            </Tag>
          </div>
          <div className="asr-drawer-summary-item">
            <div className="label">Service Visit Start</div>
            <div className="value">{formatDate(session.visitStart)}</div>
          </div>
          <div className="asr-drawer-summary-item">
            <div className="label">Service Visit End</div>
            <div className="value">{formatDate(session.visitEnd)}</div>
          </div>
          <div className="asr-drawer-summary-item">
            <div className="label">Chambers</div>
            <div className="value">{(session.chambers || []).length}</div>
          </div>
        </div>

        <Table
          rowKey="chamberId"
          columns={columns}
          dataSource={sortedChambers}
          pagination={false}
          locale={{ emptyText: <Empty description="No chambers in this session." /> }}
        />
      </Drawer>

      {activeChamber && (
        <AsrChamberIssueEditor
          visible={Boolean(activeChamber)}
          chamber={activeChamber}
          tenantId={tenantId}
          reportDetails={session.reportDetails || {}}
          visitStart={session.visitStart}
          visitEnd={session.visitEnd}
          onClose={() => setActiveChamber(null)}
          onSave={(chamberId, patch) => onSaveChamberIssues(session.id, chamberId, patch)}
        />
      )}
    </>
  );
};

export default AsrSessionDetail;
